/**
 * LandingPage — public marketing page.
 *
 * Content lives in two side-by-side JSON files (content.en.json /
 * content.ar.json) consumed via the small useLandingContent() hook.
 * That hook also drives document <html lang> + <html dir> so the
 * entire page flips between LTR and RTL when the visitor toggles
 * language from the navbar.
 *
 * Direction-aware patterns used:
 *   - Logical Tailwind utilities everywhere: ms-/me-/ps-/pe-/start-/end-/
 *     border-s-/border-e-/text-start/text-end. NO ml-/mr-/pl-/pr-/left-/
 *     right-/text-left/text-right.
 *   - Arrows that visually point a direction get `rtl:rotate-180`.
 *   - The hero slider's two-column grid swaps text/mockup columns in
 *     RTL via order classes so the image always sits on the visual
 *     "leading edge."
 *   - Native HTML inputs use `dir="auto"` so they detect content
 *     direction themselves (handy for email which stays LTR).
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import {
  HiOutlineDocumentText,
  HiOutlineCalendar,
  HiOutlineShieldCheck,
  HiOutlineUsers,
  HiOutlineCheckCircle,
  HiOutlineChatBubbleLeftRight,
  HiOutlineEnvelope,
  HiOutlinePhone,
  HiOutlinePlayCircle,
  HiOutlineBars3,
  HiOutlineXMark,
  HiOutlineGlobeAlt,
} from 'react-icons/hi2'
import { FaFacebook, FaTwitter, FaInstagram, FaLinkedin, FaAndroid, FaApple, FaWhatsapp } from 'react-icons/fa'
import ovacsLogo from '../../assets/images/logos/ovacs-logo.svg'
import DevelopedBy from '../../components/common/DevelopedBy'
import CompanyLegalLine from '../../components/common/CompanyLegalLine'
import { LEGAL_LINKS } from '../../config/brand'
import apiConfig from '../../config/apiConfig'
import documentsMock1 from '../../assets/images/mockups/documents_mock_1.png'
import connectionsMock1 from '../../assets/images/mockups/connections_mock_1.png'
import splashMock from '../../assets/images/mockups/splach_mock_1.png'
import uploadDocMock from '../../assets/images/mockups/upload_doc_mock_3.png'

// Hero section images (one per slide, indexed by slide order)
import neverLoseDocuments from '../../assets/images/hero/never-lose-documents1_100451.png'
import perfectsForEveryone from '../../assets/images/hero/perfects-for-everyone1_100455.png'
import securityLevels from '../../assets/images/hero/security-levels1_100457.png'
import daysFree from '../../assets/images/hero/180-days-free1_100500.png'

// Additional mockups for other sections
import dashboardMock1 from '../../assets/images/mockups/dashboard_mock_1.png'

import { useLandingContent } from './landing/useLandingContent'
import { useLandingSettings } from './landing/useLandingSettings'

// Slide visual defaults — used when the CMS payload doesn't override
// `gradient` or `imageUrl` for a slide.  Indexed by slide order.
const SLIDE_VISUAL_DEFAULTS = [
  { gradient: 'from-[#29AECB] via-[#01DCBA] to-[#748f89]', image: neverLoseDocuments },
  { gradient: 'from-[#2f584f] via-[#748f89] to-[#29AECB]', image: perfectsForEveryone },
  { gradient: 'from-[#748f89] via-[#29AECB] to-[#01DCBA]', image: securityLevels },
  { gradient: 'from-[#01DCBA] via-[#29AECB] to-[#748f89]', image: daysFree },
]

const LandingPage = () => {
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuth()
  const { lang, isRtl, content, toggleLang } = useLandingContent()
  const { settings: landingSettings } = useLandingSettings()
  const heroMode = landingSettings.hero_mode || 'slider'
  const languageSwitchEnabled = landingSettings.language_switch_enabled !== false
  // Logo override from CMS payload (brand.logoUrl) falls back to the bundled SVG.
  const logoSrc = content?.brand?.logoUrl || ovacsLogo

  const [email, setEmail] = useState('')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [contactForm, setContactForm] = useState({
    sender_name: '',
    sender_email: '',
    subject: 'general',
    message: '',
    category: 'contact_visitor',
  })
  const [contactSubmitting, setContactSubmitting] = useState(false)
  const [contactSuccess, setContactSuccess] = useState(false)
  const [contactError, setContactError] = useState('')

  const [isScrolled, setIsScrolled] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isHovered, setIsHovered] = useState(false)

  const slidesCount = content.hero.slides.length

  // Navbar scroll effect
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Auto-play slider
  useEffect(() => {
    if (isHovered) return undefined
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slidesCount)
    }, 8000)
    return () => clearInterval(interval)
  }, [slidesCount, isHovered])

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slidesCount)
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slidesCount) % slidesCount)
  const goToSlide = (index) => setCurrentSlide(index)

  const dashboardHref = () => {
    if (user?.role === 'superadmin') return '/superadmin'
    if (user?.role === 'assistant')  return '/superadmin/communications'
    return '/dashboard'
  }

  const handleGetStarted = () => {
    if (isAuthenticated) navigate(dashboardHref())
    else navigate('/register')
  }

  const handleSignIn = () => {
    if (isAuthenticated) navigate(dashboardHref())
    else navigate('/login')
  }

  const handleNewsletterSubmit = (e) => {
    e.preventDefault()
    // Hook up to backend newsletter endpoint when available.
    setEmail('')
  }

  // Brand overrides — CMS may replace these with editor-supplied values.
  const brand = content?.brand || {}
  const brandAndroidUrl = brand.androidUrl
    || 'https://drive.google.com/drive/folders/1JIaqThihR35PITbFkXOSbwVyIEhm3WV0?usp=sharing'
  const brandContactEmail = brand.contactEmail || 'info@ovacs.com'
  const brandContactPhone = brand.contactPhone || '+971 54 235 8420'
  const brandContactWhatsapp = brand.contactWhatsapp || '+971 54 235 8420'
  const brandWhatsappLink = brand.whatsappLink || 'https://wa.me/971542358420'
  const brandSocial = {
    facebook:  brand.facebookUrl  || '#',
    twitter:   brand.twitterUrl   || '#',
    instagram: brand.instagramUrl || '#',
    linkedin:  brand.linkedinUrl  || '#',
  }

  const handleAndroidDownload = () => {
    window.open(brandAndroidUrl, '_blank', 'noopener')
  }

  const handleIOSDownload = () => {
    window.alert(content.cta.iosAlert)
  }

  const handleContactSubmit = async (e) => {
    e.preventDefault()
    setContactSubmitting(true)
    setContactError('')
    try {
      const response = await fetch(`${apiConfig.API_BASE_URL}/contact/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm),
      })
      if (response.ok) {
        setContactSuccess(true)
        setContactForm({
          sender_name: '',
          sender_email: '',
          subject: 'general',
          message: '',
          category: 'contact_visitor',
        })
        setTimeout(() => setContactSuccess(false), 5000)
      } else {
        setContactError(content.contact.form.errorSend)
      }
    } catch {
      setContactError(content.contact.form.errorNetwork)
    } finally {
      setContactSubmitting(false)
    }
  }

  const handleContactChange = (e) => {
    setContactForm({ ...contactForm, [e.target.name]: e.target.value })
  }

  // Direction-aware arrow components for the slider buttons. We don't
  // use `rtl:rotate-180` because the buttons are positioned via start-/
  // end- and the SVG itself rotates with the direction it represents.
  const PrevArrow = isRtl ? RightChevron : LeftChevron
  const NextArrow = isRtl ? LeftChevron  : RightChevron

  const navLinks = [
    { label: content.nav.home,       href: '#home' },
    { label: content.nav.features,   href: '#features' },
    { label: content.nav.advantages, href: '#advantages' },
    { label: content.nav.faq,        href: '#learning' },
    { label: content.nav.pricing,    href: '/pricing' },
    { label: content.nav.contact,    href: '#contact' },
    { label: content.nav.tutorials,  href: '/tutorials' },
  ]

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed start-0 end-0 z-50 py-2 sm:py-4 md:py-5">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8">
          <div
            className={`rounded-xl sm:rounded-2xl transition-all duration-300 px-2 sm:px-3 md:px-4 lg:px-6 xl:px-8 ${
              isScrolled
                ? 'bg-white/80 backdrop-blur-lg shadow-xl border border-white/20'
                : 'bg-white/50 backdrop-blur-md shadow-lg border border-white/20'
            }`}
          >
            <div className="flex justify-between items-center h-14 sm:h-16">
              {/* Logo */}
              <a href="https://ovacs.com" className="flex items-center" target="_blank" rel="noopener noreferrer">
                <img src={logoSrc} alt="OVACS" className="h-8 sm:h-10 w-auto" />
              </a>

              {/* Desktop Navigation Links */}
              <div className="hidden md:flex items-center gap-6 lg:gap-8">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className={`text-sm lg:text-base font-semibold transition-colors ${
                      isScrolled ? 'text-gray-700 hover:text-[#29AECB]' : 'text-white hover:text-[#01DCBA] drop-shadow-md'
                    }`}
                  >
                    {link.label}
                  </a>
                ))}
              </div>

              {/* Desktop end cluster — language switch + sign-in */}
              <div className="hidden md:flex items-center gap-2 lg:gap-3">
                {languageSwitchEnabled && (
                  <LanguageToggle
                    lang={lang}
                    onToggle={toggleLang}
                    isScrolled={isScrolled}
                    label={content.nav.switchLanguage}
                    ariaLabel={content.nav.switchLanguageAria}
                  />
                )}
                <button
                  onClick={handleSignIn}
                  className={`px-4 lg:px-6 py-2 rounded-full transition-all text-sm lg:text-base font-semibold ${
                    isScrolled
                      ? 'bg-[#29AECB] hover:bg-[#01DCBA] text-white shadow-md'
                      : 'bg-white/90 hover:bg-white text-[#29AECB] shadow-lg backdrop-blur-sm'
                  }`}
                >
                  {isAuthenticated ? content.nav.dashboard : content.nav.signIn}
                </button>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={mobileMenuOpen ? content.nav.closeMenu : content.nav.openMenu}
                aria-expanded={mobileMenuOpen}
                className={`md:hidden p-2 transition-colors ${
                  isScrolled ? 'text-gray-700 hover:text-[#29AECB]' : 'text-white hover:text-[#01DCBA]'
                }`}
              >
                {mobileMenuOpen ? <HiOutlineXMark className="w-6 h-6" /> : <HiOutlineBars3 className="w-6 h-6" />}
              </button>
            </div>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
              <div className="md:hidden py-4 border-t border-white/20 mt-2">
                <div className="flex flex-col space-y-3">
                  {navLinks.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`font-semibold transition-colors px-2 py-2 ${
                        isScrolled ? 'text-gray-700 hover:text-[#29AECB]' : 'text-white hover:text-[#01DCBA]'
                      }`}
                    >
                      {link.label}
                    </a>
                  ))}

                  {/* Language toggle on mobile sits as a regular row (only when enabled) */}
                  {languageSwitchEnabled && (
                    <button
                      type="button"
                      onClick={() => { toggleLang(); setMobileMenuOpen(false) }}
                      aria-label={content.nav.switchLanguageAria}
                      className={`flex items-center gap-2 px-2 py-2 font-semibold transition-colors ${
                        isScrolled ? 'text-gray-700 hover:text-[#29AECB]' : 'text-white hover:text-[#01DCBA]'
                      }`}
                    >
                      <HiOutlineGlobeAlt className="w-5 h-5" />
                      <span>{content.nav.switchLanguage}</span>
                    </button>
                  )}

                  <button
                    onClick={() => { handleSignIn(); setMobileMenuOpen(false) }}
                    className={`px-6 py-2 rounded-full transition-colors text-center font-semibold ${
                      isScrolled
                        ? 'bg-[#29AECB] hover:bg-[#01DCBA] text-white'
                        : 'bg-white/90 hover:bg-white text-[#29AECB]'
                    }`}
                  >
                    {isAuthenticated ? content.nav.dashboard : content.nav.signIn}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section — either slider (default) or full-bleed video */}
      {heroMode === 'video' ? (
        <HeroVideoBlock
          videoUrl={landingSettings.hero_video_url}
          posterUrl={landingSettings.hero_video_poster_url}
          gradientFallback={SLIDE_VISUAL_DEFAULTS[0].gradient}
          text={content.hero?.video || content.hero?.slides?.[0] || {}}
          ctaLabel={content.hero?.ctaGetStarted}
          onCta={handleGetStarted}
          lang={lang}
          isRtl={isRtl}
        />
      ) : (
      <section id="home" className="overflow-hidden">
        <div className="w-full">
          <div className="relative">
            <div
              className="relative h-[900px] sm:h-[1000px] md:h-[600px] lg:h-[700px] overflow-hidden"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {content.hero.slides.map((slide, index) => {
                // CMS can override the gradient and image per slide.  Fall back
                // to the bundled visual defaults when not provided.
                const defaults = SLIDE_VISUAL_DEFAULTS[index] || SLIDE_VISUAL_DEFAULTS[0]
                const visual = {
                  gradient: slide.gradient || defaults.gradient,
                  image: slide.imageUrl || defaults.image,
                }
                // Slide-transform direction depends on RTL: in RTL, "next"
                // should arrive from the start (right) instead of the end.
                const isActive = index === currentSlide
                const isBefore = index < currentSlide
                const directionMod = isRtl ? -1 : 1
                let translateClass = 'translate-x-0 opacity-100'
                if (!isActive) {
                  translateClass = isBefore
                    ? (directionMod === 1 ? '-translate-x-full opacity-0' : 'translate-x-full opacity-0')
                    : (directionMod === 1 ? 'translate-x-full opacity-0'  : '-translate-x-full opacity-0')
                }
                return (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-all duration-700 ease-in-out ${translateClass}`}
                  >
                    <div className={`w-full h-full bg-gradient-to-br ${visual.gradient} relative`}>
                      <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
                        <div className="h-full grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-6 lg:gap-12 items-start md:items-center sm:pt-12 md:pt-10">
                          {/* Text Content */}
                          <div className="text-white space-y-1 sm:space-y-2 md:space-y-4 lg:space-y-5 z-10 py-2 md:py-0 text-start">
                            <div className="inline-block bg-white bg-opacity-20 backdrop-blur-sm px-2 sm:px-3 py-1 sm:py-1.5 rounded-full">
                              <p className="text-xs sm:text-sm font-semibold tracking-wide uppercase">
                                {slide.subtitle}
                              </p>
                            </div>

                            <h1
                              className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight"
                              style={{ fontFamily: lang === 'ar' ? "'Cairo', sans-serif" : "'Reem Kufi', sans-serif" }}
                            >
                              {slide.title}
                            </h1>

                            <p
                              className="text-xs sm:text-sm md:text-base lg:text-lg text-white text-opacity-95 leading-relaxed max-w-full md:max-w-lg"
                              style={{ fontFamily: lang === 'ar' ? "'Cairo', sans-serif" : "'Poppins', sans-serif" }}
                            >
                              {slide.description}
                            </p>

                            <button
                              onClick={handleGetStarted}
                              className="bg-white text-gray-900 hover:bg-gray-100 px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg md:rounded-xl font-semibold text-xs sm:text-sm md:text-base shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 inline-flex items-center gap-2 group"
                            >
                              <HiOutlinePlayCircle className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" />
                              {content.hero.ctaGetStarted}
                            </button>
                          </div>

                          {/* Phone Mockups */}
                          <div className="relative h-auto md:h-full flex items-center justify-center md:justify-end py-2 md:py-0">
                            <div className="relative w-full max-w-2xl sm:max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl">
                              <div className="absolute top-1/2 start-1/2 transition-all duration-700">
                                <img
                                  src={visual.image}
                                  alt={`${slide.title} - Front`}
                                  className="h-auto w-[650px] sm:w-[800px] md:w-[1000px] lg:w-[1200px] xl:w-[1400px]"
                                  style={{
                                    transform: `
                                      translate(${isRtl ? '50%' : '-50%'}, -50%)
                                      translateX(0px) translateY(0px)
                                      scale(${isActive ? 2.4 : 1.7})
                                    `,
                                    filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.3))',
                                    opacity: isActive ? 1 : 0.5,
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}

              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="hidden md:flex absolute start-4 lg:start-8 top-1/2 -translate-y-1/2 z-20 bg-white bg-opacity-20 hover:bg-opacity-30 backdrop-blur-sm text-white p-2 sm:p-3 rounded-full transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white items-center justify-center"
                aria-label={content.hero.previousSlide}
              >
                <PrevArrow />
              </button>

              <button
                onClick={nextSlide}
                className="hidden md:flex absolute end-4 lg:end-8 top-1/2 -translate-y-1/2 z-20 bg-white bg-opacity-20 hover:bg-opacity-30 backdrop-blur-sm text-white p-2 sm:p-3 rounded-full transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white items-center justify-center"
                aria-label={content.hero.nextSlide}
              >
                <NextArrow />
              </button>

              {/* Dot Indicators */}
              <div className="absolute bottom-6 sm:bottom-8 md:bottom-10 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 z-20 flex gap-2 sm:gap-3">
                {content.hero.slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`transition-all duration-300 rounded-full focus:outline-none focus:ring-2 focus:ring-white ${
                      index === currentSlide
                        ? 'w-8 sm:w-10 md:w-12 h-2.5 sm:h-3 bg-white shadow-lg'
                        : 'w-2.5 sm:w-3 h-2.5 sm:h-3 bg-white bg-opacity-50 hover:bg-opacity-75'
                    }`}
                    aria-label={content.hero.goToSlide.replace('{{n}}', index + 1)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Features Section */}
      <section id="features" className="py-12 sm:py-16 lg:py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="relative flex items-center justify-center h-[300px] sm:h-[400px] lg:h-[500px] order-2 md:order-1">
              <div
                className="relative z-10"
                style={{ filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.2))' }}
              >
                <img
                  src={dashboardMock1}
                  alt={content.features.heading}
                  className="w-56 sm:w-64 lg:w-72 h-auto scale-[0.85] sm:scale-100"
                />
              </div>
              <div className="hidden lg:block absolute top-1/2 start-0 w-96 h-96 bg-gradient-to-br from-white to-cyan-50 rounded-full -z-10 transform -translate-x-1/4 rtl:translate-x-1/4 -translate-y-1/2 opacity-60"></div>
            </div>

            <div className="text-center md:text-start order-1 md:order-2">
              <SectionEyebrow>{content.features.eyebrow}</SectionEyebrow>
              <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 sm:mb-8">
                {content.features.heading}
              </h3>

              <div className="space-y-4 sm:space-y-6">
                {content.features.items.map((item, idx) => {
                  const Icon = [HiOutlineDocumentText, HiOutlineUsers, HiOutlineShieldCheck][idx] || HiOutlineDocumentText
                  return (
                    <div key={idx} className="flex items-start gap-3 sm:gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-600" />
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-base sm:text-lg">{item.title}</h4>
                        <p className="text-gray-600 text-sm sm:text-base">{item.body}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section id="advantages" className="py-12 sm:py-16 lg:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="text-center md:text-start">
              <SectionEyebrow>{content.advantages.eyebrow}</SectionEyebrow>
              <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 sm:mb-8">
                {content.advantages.heading}
              </h3>

              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <HiOutlineShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-600" />
                  </div>
                </div>
                <div>
                  <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">{content.advantages.cardTitle}</h4>
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{content.advantages.cardBody}</p>
                </div>
              </div>
            </div>

            <div className="relative flex items-center justify-center h-[300px] sm:h-[400px] lg:h-[500px]">
              <div className="relative z-10" style={{ filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.2))' }}>
                <img
                  src={documentsMock1}
                  alt={content.advantages.cardTitle}
                  className="w-56 sm:w-64 lg:w-72 h-auto scale-[0.85] sm:scale-100"
                />
              </div>
              <div className="hidden lg:block absolute top-1/2 end-0 w-96 h-96 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-full -z-10 transform translate-x-1/4 rtl:-translate-x-1/4 -translate-y-1/2 opacity-60"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Why-Choose Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="relative order-2 md:order-1 flex items-center justify-center h-[300px] sm:h-[400px] lg:h-[500px]">
              <div className="relative z-10" style={{ filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.2))' }}>
                <img
                  src={connectionsMock1}
                  alt={content.whyChoose.heading}
                  className="w-56 sm:w-64 lg:w-72 h-auto scale-[0.85] sm:scale-100"
                />
              </div>
              <div className="hidden lg:block absolute top-1/2 start-0 w-96 h-96 bg-gradient-to-br from-white to-cyan-50 rounded-full -z-10 transform -translate-x-1/4 rtl:translate-x-1/4 -translate-y-1/2 opacity-60"></div>
            </div>

            <div className="order-1 md:order-2 text-center md:text-start">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <HiOutlineChatBubbleLeftRight className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">{content.whyChoose.heading}</h3>
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base mb-3">{content.whyChoose.body}</p>
                  <ul className="space-y-2 text-gray-600 text-sm sm:text-base">
                    {content.whyChoose.bullets.map((b, i) => (
                      <li key={i} className="flex items-start">
                        <HiOutlineCheckCircle className="w-5 h-5 text-cyan-600 me-2 flex-shrink-0 mt-0.5" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <SectionEyebrow className="mx-auto">{content.testimonials.eyebrow}</SectionEyebrow>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
              {content.testimonials.heading}
            </h3>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
            {content.testimonials.items.map((t, i) => (
              <div key={i} className="bg-white p-5 sm:p-6 rounded-xl shadow-lg border border-gray-100">
                <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div
                    className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-md flex-shrink-0 ${
                      i === 0
                        ? 'bg-gradient-to-br from-cyan-400 to-blue-500'
                        : 'bg-gradient-to-br from-brand to-brand-bright'
                    }`}
                  >
                    {t.initials}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm sm:text-base">{t.name}</h4>
                    <p className="text-xs sm:text-sm text-gray-500" aria-label="5 stars">★★★★★</p>
                  </div>
                </div>
                <p className="text-gray-600 italic text-sm sm:text-base">{t.quote}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="learning" className="py-12 sm:py-16 lg:py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <SectionEyebrow className="mx-auto">{content.faq.eyebrow}</SectionEyebrow>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">{content.faq.heading}</h3>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-5xl mx-auto">
            {content.faq.items.map((item, idx) => {
              const isLast = idx === content.faq.items.length - 1
              const isFilled = idx % 3 === 0 || idx === 4 || idx === 7
              const tone = idx % 6 === 4 ? 'teal' : 'cyan'
              if (isLast) {
                return (
                  <div
                    key={idx}
                    className="bg-gradient-to-r from-cyan-500 via-blue-500 to-brand-soft0 p-5 sm:p-6 rounded-2xl sm:col-span-2 shadow-xl hover:shadow-2xl transition-shadow transform hover:scale-105 transition-transform"
                  >
                    <h4 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">{item.q}</h4>
                    <p className="text-white text-opacity-95 text-sm sm:text-base">{item.a}</p>
                  </div>
                )
              }
              return isFilled ? (
                <div
                  key={idx}
                  className={`bg-gradient-to-br ${
                    tone === 'teal' ? 'from-teal-400 via-cyan-500 to-blue-500' : 'from-cyan-400 via-cyan-500 to-blue-500'
                  } p-5 sm:p-6 rounded-2xl shadow-xl hover:shadow-2xl transition-shadow transform hover:scale-105 transition-transform`}
                >
                  <h4 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">{item.q}</h4>
                  <p className="text-white text-opacity-95 text-sm sm:text-base">{item.a}</p>
                </div>
              ) : (
                <div
                  key={idx}
                  className="bg-white border-2 border-cyan-200 p-5 sm:p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow transform hover:scale-105 transition-transform"
                >
                  <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">{item.q}</h4>
                  <p className="text-gray-700 text-sm sm:text-base">{item.a}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-12 sm:py-16 lg:py-20 px-4 bg-black text-white overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="text-center md:text-start">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 sm:mb-6">{content.cta.heading}</h2>
              <p className="text-gray-300 mb-6 sm:mb-8 text-base sm:text-lg">{content.cta.body}</p>

              <div className="flex flex-wrap gap-3 sm:gap-4 justify-center md:justify-start">
                <button
                  onClick={handleAndroidDownload}
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg transition-all flex items-center gap-2 sm:gap-3 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <FaAndroid className="w-5 h-5 sm:w-6 sm:h-6" />
                  <div className="text-start">
                    <div className="text-xs opacity-90">{content.cta.downloadFor}</div>
                    <div className="font-bold text-sm sm:text-base">{content.cta.android}</div>
                  </div>
                </button>

                <button
                  onClick={handleIOSDownload}
                  className="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg transition-all flex items-center gap-2 sm:gap-3 shadow-lg hover:shadow-xl transform hover:scale-105 relative"
                >
                  <FaApple className="w-5 h-5 sm:w-6 sm:h-6" />
                  <div className="text-start">
                    <div className="text-xs opacity-90">{content.cta.downloadFor}</div>
                    <div className="font-bold text-sm sm:text-base">{content.cta.ios}</div>
                  </div>
                  <span className="absolute -top-2 -end-2 bg-cyan-500 text-white text-xs px-2 py-1 rounded-full">
                    {content.cta.soonBadge}
                  </span>
                </button>
              </div>

              <div className="mt-5 sm:mt-6 pt-5 sm:pt-6 border-t border-gray-700">
                <p className="text-gray-400 mb-3 sm:mb-4 text-sm sm:text-base">
                  {isAuthenticated ? content.cta.altDashboard : content.cta.altWeb}
                </p>
                <button
                  onClick={handleGetStarted}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg transition-colors mx-auto md:mx-0 block md:inline-block"
                >
                  {isAuthenticated ? content.cta.dashboardButton : content.cta.webButton}
                </button>
              </div>
            </div>

            <div className="relative flex items-center justify-center h-[350px] sm:h-[450px] lg:h-[500px]">
              <div className="relative w-full max-w-lg">
                <div
                  className="absolute top-1/2 start-1/2"
                  style={{
                    zIndex: 1,
                    transform: `translate(${isRtl ? '50%' : '-50%'}, -50%) translateX(${isRtl ? -180 : 180}px) translateY(-5px) scale(1.15)`,
                    filter: 'drop-shadow(0 20px 40px rgba(79, 209, 197, 0.2))',
                  }}
                >
                  <img src={uploadDocMock} alt="Upload Documents" className="w-56 sm:w-64 lg:w-72 h-auto" />
                </div>

                <div
                  className="absolute top-1/2 start-1/2"
                  style={{
                    zIndex: 2,
                    transform: `translate(${isRtl ? '50%' : '-50%'}, -50%) translateX(${isRtl ? 10 : -10}px) scale(0.95)`,
                    filter: 'drop-shadow(0 30px 60px rgba(79, 209, 197, 0.3))',
                  }}
                >
                  <img src={splashMock} alt="OVACS Mobile App" className="w-56 sm:w-64 lg:w-72 h-auto" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute top-0 end-0 w-96 h-96 border border-gray-800 rounded-full transform translate-x-1/2 rtl:-translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 start-0 w-96 h-96 border border-gray-800 rounded-full transform -translate-x-1/2 rtl:translate-x-1/2 translate-y-1/2"></div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-12 sm:py-16 lg:py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <SectionEyebrow className="mx-auto">{content.contact.eyebrow}</SectionEyebrow>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              {content.contact.heading}
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base px-4">
              {content.contact.lead}
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            {contactSuccess && (
              <div className="mb-6 p-4 bg-success-soft border border-success/30 rounded-lg text-green-800 text-center text-sm sm:text-base">
                {content.contact.form.success}
              </div>
            )}
            {contactError && (
              <div role="alert" className="mb-6 p-4 bg-danger-soft border border-danger/30 rounded-lg text-red-800 text-center text-sm sm:text-base">
                {contactError}
              </div>
            )}

            <form onSubmit={handleContactSubmit} className="bg-white border-2 border-gray-200 rounded-2xl p-5 sm:p-6 lg:p-8 shadow-lg">
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label htmlFor="sender_name" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    {content.contact.form.nameLabel}
                  </label>
                  <input
                    type="text"
                    id="sender_name"
                    name="sender_name"
                    value={contactForm.sender_name}
                    onChange={handleContactChange}
                    required
                    dir="auto"
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                    placeholder={content.contact.form.namePlaceholder}
                  />
                </div>

                <div>
                  <label htmlFor="sender_email" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    {content.contact.form.emailLabel}
                  </label>
                  <input
                    type="email"
                    id="sender_email"
                    name="sender_email"
                    value={contactForm.sender_email}
                    onChange={handleContactChange}
                    required
                    dir="ltr"
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                    placeholder={content.contact.form.emailPlaceholder}
                  />
                </div>
              </div>

              <div className="mb-4 sm:mb-6">
                <label htmlFor="subject" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  {content.contact.form.subjectLabel}
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={contactForm.subject}
                  onChange={handleContactChange}
                  required
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                >
                  <option value="general">{content.contact.form.subjects.general}</option>
                  <option value="support">{content.contact.form.subjects.support}</option>
                  <option value="bug">{content.contact.form.subjects.bug}</option>
                  <option value="suggestion">{content.contact.form.subjects.suggestion}</option>
                  <option value="sales">{content.contact.form.subjects.sales}</option>
                </select>
              </div>

              <div className="mb-4 sm:mb-6">
                <label htmlFor="message" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  {content.contact.form.messageLabel}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={contactForm.message}
                  onChange={handleContactChange}
                  required
                  rows="6"
                  dir="auto"
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all resize-none"
                  placeholder={content.contact.form.messagePlaceholder}
                />
              </div>

              <button
                type="submit"
                disabled={contactSubmitting}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold px-6 sm:px-8 py-3 sm:py-4 rounded-lg transition-all shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                {contactSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {content.contact.form.submitting}
                  </>
                ) : (
                  <>
                    <HiOutlineEnvelope className="w-5 h-5" />
                    {content.contact.form.submit}
                  </>
                )}
              </button>
            </form>

            <div className="mt-12 grid md:grid-cols-4 gap-6 text-center">
              <div className="p-6 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <HiOutlineEnvelope className="w-6 h-6 text-cyan-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{content.contact.cards.emailTitle}</h4>
                <p className="text-gray-600 text-sm" dir="ltr">{brandContactEmail}</p>
              </div>

              <div className="p-6 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <HiOutlinePhone className="w-6 h-6 text-cyan-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{content.contact.cards.phoneTitle}</h4>
                <p className="text-gray-600 text-sm" dir="ltr">{brandContactPhone}</p>
              </div>

              <a href={brandWhatsappLink} target="_blank" rel="noopener noreferrer" className="p-6 bg-gray-50 rounded-xl hover:bg-success-soft transition-colors">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaWhatsapp className="w-6 h-6 text-success" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{content.contact.cards.whatsappTitle}</h4>
                <p className="text-gray-600 text-sm" dir="ltr">{brandContactWhatsapp}</p>
              </a>

              <div className="p-6 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <HiOutlineChatBubbleLeftRight className="w-6 h-6 text-cyan-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{content.contact.cards.chatTitle}</h4>
                <p className="text-gray-600 text-sm">{content.contact.cards.chatBody}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-8 mb-8">
            {/* Company Info */}
            <div className="md:col-span-2">
              <a href="https://ovacs.com" className="flex items-center mb-4" target="_blank" rel="noopener noreferrer">
                <img src={logoSrc} alt="OVACS" className="h-10 w-auto brightness-0 invert" />
              </a>
              <div className="space-y-2 text-gray-400">
                <div className="flex items-center gap-2">
                  <HiOutlineEnvelope className="w-5 h-5" />
                  <span dir="ltr">{brandContactEmail}</span>
                </div>
                <div className="flex items-center gap-2">
                  <HiOutlinePhone className="w-5 h-5" />
                  <span dir="ltr">{brandContactPhone}</span>
                </div>
                <a href={brandWhatsappLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-green-400 transition-colors">
                  <FaWhatsapp className="w-5 h-5" />
                  <span dir="ltr">{brandContactWhatsapp}</span>
                </a>
              </div>
              <div className="flex gap-4 mt-4">
                <a href={brandSocial.facebook} aria-label="Facebook" target={brandSocial.facebook === '#' ? undefined : '_blank'} rel="noopener noreferrer" className="text-gray-400 hover:text-cyan-400 transition-colors">
                  <FaFacebook className="w-5 h-5" />
                </a>
                <a href={brandSocial.twitter} aria-label="Twitter" target={brandSocial.twitter === '#' ? undefined : '_blank'} rel="noopener noreferrer" className="text-gray-400 hover:text-cyan-400 transition-colors">
                  <FaTwitter className="w-5 h-5" />
                </a>
                <a href={brandSocial.instagram} aria-label="Instagram" target={brandSocial.instagram === '#' ? undefined : '_blank'} rel="noopener noreferrer" className="text-gray-400 hover:text-cyan-400 transition-colors">
                  <FaInstagram className="w-5 h-5" />
                </a>
                <a href={brandSocial.linkedin} aria-label="LinkedIn" target={brandSocial.linkedin === '#' ? undefined : '_blank'} rel="noopener noreferrer" className="text-gray-400 hover:text-cyan-400 transition-colors">
                  <FaLinkedin className="w-5 h-5" />
                </a>
              </div>
            </div>

            <FooterColumn title={content.footer.columns.links}     items={content.footer.linksItems} />
            {/* Legal links are code-owned (config/brand.js), not CMS-managed. */}
            <FooterColumn
              title={content.footer.columns.legal}
              items={LEGAL_LINKS.map((l) => ({ label: l[lang] || l.en, href: l.href }))}
            />
            <FooterColumn title={content.footer.columns.resources} items={content.footer.resourcesItems} />
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="font-semibold text-white mb-2">{content.footer.newsletter.title}</h3>
                <p className="text-gray-400 text-sm">{content.footer.newsletter.subtitle}</p>
              </div>
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={content.footer.newsletter.placeholder}
                  dir="ltr"
                  className="w-full sm:w-64 px-3 sm:px-4 py-2 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent placeholder-gray-500 text-sm sm:text-base"
                  required
                />
                <button
                  type="submit"
                  className="bg-cyan-500 hover:bg-cyan-600 text-white px-4 sm:px-6 py-2 rounded-lg transition-colors whitespace-nowrap text-sm sm:text-base"
                >
                  {content.footer.newsletter.submit}
                </button>
              </form>
            </div>
          </div>

          {/* Legal bar — company, licence and address at the start, developer
              credit at the end. Stacks and centres on phones. Neither part is
              CMS-managed: the company line must match the trade licence. */}
          <div className="border-t border-gray-800 mt-8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-start text-gray-500 text-sm">
            <CompanyLegalLine lang={lang} />
            <DevelopedBy tone="dark" lang={lang} />
          </div>
        </div>
      </footer>
    </div>
  )
}

// ───────── helpers ──────────────────────────────────────────────────────

function SectionEyebrow({ children, className = '' }) {
  return (
    <h2
      className={`inline-block text-xl sm:text-2xl font-bold mb-3 sm:mb-4 ${className}`}
      style={{
        background: 'linear-gradient(90deg, #4FD1C5 0%, #21A6C3 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        letterSpacing: '0.1em',
      }}
    >
      {children}
    </h2>
  )
}

function FooterColumn({ title, items }) {
  return (
    <div>
      <h3 className="font-semibold text-white mb-4">{title}</h3>
      <ul className="space-y-2 text-gray-400">
        {items.map((item) => (
          <li key={item.label}>
            <a
              href={item.href}
              {...(item.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              className="hover:text-cyan-400 transition-colors"
            >
              {item.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

function LanguageToggle({ lang, onToggle, isScrolled, label, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={ariaLabel}
      lang={lang === 'en' ? 'ar' : 'en'}
      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold transition-all ${
        isScrolled
          ? 'text-gray-700 hover:text-[#29AECB] hover:bg-cyan-50'
          : 'text-white hover:text-[#01DCBA] hover:bg-white/10 backdrop-blur-sm drop-shadow-md'
      }`}
    >
      <HiOutlineGlobeAlt className="w-4 h-4" />
      <span>{label}</span>
    </button>
  )
}

function LeftChevron() {
  return (
    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  )
}

function RightChevron() {
  return (
    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  )
}

/**
 * HeroVideoBlock — alternative hero rendered when settings.hero_mode === 'video'.
 *
 * Full-bleed muted autoplay loop video as background with the same text-card
 * styling used by the slider's text panel (eyebrow chip + Reem-Kufi title +
 * description + CTA).  Always lays a gradient + poster image behind the
 * video so the page never looks blank on slow connections, when the video
 * fails to load, or when the user has prefers-reduced-motion set.
 *
 * Props:
 *   videoUrl        absolute or relative URL to an MP4 (or any <video> source)
 *   posterUrl       optional still image shown until the video plays
 *   gradientFallback Tailwind gradient class string used behind the video
 *   text            { subtitle, title, description } pulled from the CMS
 *                   payload at hero.video, falling back to hero.slides[0]
 *   ctaLabel        button label
 *   onCta           click handler (same as slider's Get Started)
 *   lang / isRtl    for font selection + transform direction
 */
function HeroVideoBlock({ videoUrl, posterUrl, gradientFallback, text, ctaLabel, onCta, lang, isRtl }) {
  return (
    <section id="home" className="overflow-hidden">
      <div className="w-full">
        <div className="relative h-[900px] sm:h-[1000px] md:h-[600px] lg:h-[700px] overflow-hidden">
          {/* Background — gradient first, then poster image, then video on top */}
          <div className={`absolute inset-0 bg-gradient-to-br ${gradientFallback}`} />
          {posterUrl && (
            <img
              src={posterUrl}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover opacity-60"
            />
          )}
          {videoUrl && (
            <video
              src={videoUrl}
              poster={posterUrl || undefined}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-black/30" />

          {/* Text overlay — uses Slide-style styling */}
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 z-10">
            <div className="h-full flex items-center md:items-center">
              <div className="text-white space-y-2 md:space-y-4 lg:space-y-5 text-start max-w-2xl">
                {text?.subtitle && (
                  <div className="inline-block bg-white bg-opacity-20 backdrop-blur-sm px-3 py-1.5 rounded-full">
                    <p className="text-xs sm:text-sm font-semibold tracking-wide uppercase">{text.subtitle}</p>
                  </div>
                )}
                {text?.title && (
                  <h1
                    className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight"
                    style={{ fontFamily: lang === 'ar' ? "'Cairo', sans-serif" : "'Reem Kufi', sans-serif" }}
                  >
                    {text.title}
                  </h1>
                )}
                {text?.description && (
                  <p
                    className="text-sm sm:text-base md:text-lg text-white text-opacity-95 leading-relaxed max-w-full md:max-w-xl"
                    style={{ fontFamily: lang === 'ar' ? "'Cairo', sans-serif" : "'Poppins', sans-serif" }}
                  >
                    {text.description}
                  </p>
                )}
                {ctaLabel && (
                  <button
                    onClick={onCta}
                    className="bg-white text-gray-900 hover:bg-gray-100 px-5 md:px-6 py-2.5 md:py-3 rounded-lg md:rounded-xl font-semibold text-sm md:text-base shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 inline-flex items-center gap-2 group"
                  >
                    <HiOutlinePlayCircle className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" />
                    {ctaLabel}
                  </button>
                )}
                {isRtl && null /* isRtl currently used only by slider arrows; reserved for future RTL tweaks */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default LandingPage
